import { Add, Delete } from '@mui/icons-material';
import { TabPanel } from '@mui/lab';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { AddFieldValues } from '../AddFieldTemplateDialog';
import {
  FieldAttributesOptions,
  FieldTemplate,
  FieldType,
  HL7ImportRule,
  OptionSourceType,
} from 'api/models/FieldTemplate';
import { useEffect, useState } from 'react';
import useQuery from 'hooks/useQuery';
import { GeneralIds, ModelTypes } from 'types/types';
import { useParams } from 'react-router-dom';
import ClearableSelect from 'components/ClearableSelect/ClearableSelect';
import OptionAutocompleteField from 'routes/FormTemplateEditor/FormEditor/FieldTemplate/Fields/OptionAutocompleteField';
import { produce } from 'immer';

type Correspondance = {
  label: string;
  value: string;
};

function getCorrespondance(mappings: { [key: string]: string }) {
  return Object.entries(mappings).map(([key, value]) => ({
    label: key,
    value: value.toString(),
  }));
}

function getMappings(correspondance: Correspondance[]) {
  return correspondance.reduce((obj, item) => {
    // @ts-ignore
    obj[item.label as any] = item.value;
    return obj;
  }, {});
}

interface CorrespondingTabProps {
  formik: FormikProps<AddFieldValues>;
  disabled: boolean;
  fieldType: FieldType;
}

export default function CorrespondingTab({
  formik,
  disabled,
  fieldType,
}: CorrespondingTabProps) {
  const [mappings, setMappings] = useState(formik.values.mappings!);
  const { t } = useTranslation();
  const { projectId } = useParams<GeneralIds>();
  const isSelect = fieldType === FieldType.Select;
  const [options, setOptions] = useState<FieldAttributesOptions[]>();
  const sourceFieldId = formik.values.optionsSourceFieldTemplateId;
  const [isAddValues, setIsAddValues] = useState(
    Object.keys(formik.values.mappings?.standardCodeLibrary?.valueMapping ?? {})
      .length > 0
      ? true
      : false,
  );
  const [correspondance, setCorrespondance] = useState<Correspondance[]>(
    getCorrespondance(
      formik.values.mappings?.standardCodeLibrary?.valueMapping ?? {},
    ),
  );

  const { data: fields } = useQuery<FieldTemplate[]>({
    type: ModelTypes.fieldTemplate,
    ids: {
      projectId,
      id: sourceFieldId,
    },
    skip: !sourceFieldId,
  });

  useEffect(() => {
    const field = fields?.[0];
    if (field) {
      setOptions(field.fieldAttributes.options ?? []);
    } else {
      setOptions(formik.values.options ?? []);
    }
  }, [
    fields,
    formik.values.options,
    formik.values.optionsSourceFieldTemplateId,
  ]);

  useEffect(() => {
    const newMappings = produce(mappings, draft => {
      if (!draft?.standardCodeLibrary) {
        draft.standardCodeLibrary = {
          fieldIdentifier: '',
          valueMapping: getMappings(correspondance),
        };
      }
      draft.standardCodeLibrary.valueMapping = getMappings(correspondance);
    });

    formik.setFieldValue('mappings', newMappings);
    // eslint-disable-next-line
  }, [correspondance]);

  return (
    <TabPanel value="3" sx={{ p: 0, pt: 2, minHeight: 500 }}>
      <Box display={'flex'} justifyContent={'space-between'} mb={2}>
        <Typography sx={{ mb: 1 }} variant="h6">
          {t('dialogs.addField.standardCodeLibraryCorresponding')}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              disabled={disabled}
              checked={isAddValues}
              value={isAddValues}
              onChange={event => setIsAddValues(event.target.checked)}
            />
          }
          label={t('dialogs.addField.correspondingValues')}
        />
      </Box>
      <Box display="flex" flexDirection={'row'}>
        <TextField
          disabled={disabled}
          value={mappings?.standardCodeLibrary?.fieldIdentifier}
          onChange={e => {
            const newMappings = produce(mappings, draft => {
              if (!draft?.standardCodeLibrary) {
                draft.standardCodeLibrary = {
                  fieldIdentifier: '',
                };
              }
              draft.standardCodeLibrary.fieldIdentifier = e.target.value;
            });
            setMappings(newMappings);
            formik.setFieldValue('mappings', newMappings);
          }}
          fullWidth
          label={t('general.field')}
        />
      </Box>

      {isAddValues && (
        <>
          <Typography sx={{ mt: 2 }} variant="h6">
            {t('general.values')}
          </Typography>
          {correspondance.map((item, index) => (
            <Grid container spacing={1} alignItems={'center'}>
              <Grid item xs={5.5} mt={2}>
                {isSelect ? (
                  formik.values.optionsSourceType ===
                  OptionSourceType.CodeLibrary ? (
                    <OptionAutocompleteField
                      onChange={val => {
                        const newCorrespondance = [...correspondance];
                        newCorrespondance[index].label = val as string;
                        setCorrespondance(newCorrespondance);
                      }}
                      value={item.label}
                      optionsSourceCodeLibraryId={
                        formik.values.optionsSourceCodeLibraryId
                      }
                      label={t('forms.fieldAttributes.defaultValue')}
                      defaultValue={''}
                      isMultiSelect={false}
                    />
                  ) : (
                    <ClearableSelect
                      disabled={disabled}
                      fullWidth
                      key={`correspondance-label-${index}`}
                      label={t('general.label')}
                      value={item.label}
                      onChange={event => {
                        const newCorrespondance = [...correspondance];
                        newCorrespondance[index].label = event.target.value;
                        setCorrespondance(newCorrespondance);
                      }}
                    >
                      {options?.map(option => {
                        return (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        );
                      })}
                    </ClearableSelect>
                  )
                ) : (
                  <TextField
                    disabled={disabled}
                    fullWidth
                    key={`correspondance-label-${index}`}
                    label={t('general.label')}
                    value={item.label}
                    onChange={event => {
                      const newCorrespondance = [...correspondance];
                      newCorrespondance[index].label = event.target.value;
                      setCorrespondance(newCorrespondance);
                    }}
                  />
                )}
              </Grid>
              <Grid item xs={5.5} mt={2}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  key={`correspondance-value-${index}`}
                  label={t('general.value')}
                  value={item.value}
                  onChange={event => {
                    const newCorrespondance = [...correspondance];
                    newCorrespondance[index].value = event.target.value;
                    setCorrespondance(newCorrespondance);
                  }}
                />
              </Grid>
              <Grid item xs={1}>
                <IconButton
                  disabled={disabled}
                  sx={{ mt: 2 }}
                  onClick={() => {
                    setCorrespondance(
                      correspondance.filter((c, i) => i !== index),
                    );
                  }}
                >
                  <Delete />
                </IconButton>
              </Grid>
            </Grid>
          ))}

          <Box width={'flex'} display={'flex'} justifyContent={'end'} mt={2}>
            <Button
              variant="text"
              disabled={disabled}
              onClick={() => {
                setCorrespondance([
                  ...correspondance,
                  { label: '', value: '' },
                ]);
              }}
            >
              <Add fontSize="small" />
              {t('dialogs.addField.addNewCorrespondence')}
            </Button>
          </Box>
        </>
      )}
      <Typography sx={{ mb: 1, mt: 2 }} variant="h6">
        {t('dialogs.addField.externalCommunication')}
      </Typography>
      <Grid container spacing={1} alignItems={'center'}>
        <Grid item xs={6} mt={2}>
          <TextField
            disabled={disabled}
            value={mappings?.externalCommunication?.hl7Identifier}
            required={
              mappings?.externalCommunication?.hl7ImportRule ? true : false
            }
            onChange={e => {
              const newMappings = produce(mappings, draft => {
                if (!draft?.externalCommunication) {
                  draft.externalCommunication = {
                    hl7Identifier: '',
                    hl7OverrideEnabled: true,
                    hl7ImportRule: HL7ImportRule.LAST,
                  };
                }
                draft.externalCommunication.hl7Identifier = e.target.value;
              });
              setMappings(newMappings);
              formik.setFieldValue('mappings', newMappings);
            }}
            fullWidth
            label={t('dialogs.addField.externalId')}
          />
        </Grid>
        <Grid item xs={6} mt={2}>
          <ClearableSelect
            disabled={disabled}
            onClear={() => {
              const newMappings = produce(mappings, draft => {
                if (!draft?.externalCommunication) {
                  draft.externalCommunication = {
                    hl7OverrideEnabled: true,
                    hl7ImportRule: HL7ImportRule.LAST,
                  };
                }
                // @ts-ignore
                draft.externalCommunication.hl7ImportRule = undefined;
              });
              setMappings(newMappings);
              formik.setFieldValue('mappings', newMappings);
            }}
            fullWidth
            label={t('dialogs.addField.importRule')}
            value={mappings?.externalCommunication?.hl7ImportRule}
            onChange={e => {
              const newMappings = produce(mappings, draft => {
                if (!draft?.externalCommunication) {
                  draft.externalCommunication = {
                    hl7Identifier: '',
                    hl7OverrideEnabled: true,
                    hl7ImportRule: HL7ImportRule.LAST,
                  };
                }
                // @ts-ignore
                draft.externalCommunication.hl7ImportRule = e.target.value;
              });
              setMappings(newMappings);
              formik.setFieldValue('mappings', newMappings);
            }}
          >
            {Object.keys(HL7ImportRule)?.map(option => {
              return (
                // @ts-ignore
                <MenuItem key={option} value={HL7ImportRule[option]}>
                  {t(`dialogs.addField.importRules.${option}`)}
                </MenuItem>
              );
            })}
          </ClearableSelect>
        </Grid>
      </Grid>
      <Box display={'flex'} justifyContent={'end'} mb={2}>
        <FormControlLabel
          control={
            <Checkbox
              disabled={disabled}
              checked={
                !(mappings.externalCommunication?.hl7OverrideEnabled ?? true)
              }
              value={
                !(mappings.externalCommunication?.hl7OverrideEnabled ?? true)
              }
              onChange={e => {
                const newMappings = produce(mappings, draft => {
                  if (!draft?.externalCommunication) {
                    draft.externalCommunication = {
                      hl7Identifier: '',
                      hl7OverrideEnabled: true,
                      hl7ImportRule: HL7ImportRule.LAST,
                    };
                  }
                  // @ts-ignore
                  draft.externalCommunication.hl7OverrideEnabled =
                    !e.target.checked;
                });
                setMappings(newMappings);
                formik.setFieldValue('mappings', newMappings);
              }}
            />
          }
          label={t('dialogs.addField.noOverride')}
        />
      </Box>
    </TabPanel>
  );
}
